import React, { useState, useEffect } from 'react';
import { Mail, RefreshCw, X, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { sendVerificationEmail } from '@/firebase/auth';
import toast from 'react-hot-toast';

const EmailVerificationBanner: React.FC = () => {
  const { firebaseUser, refreshUser } = useAuthContext();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Check sessionStorage on mount
  useEffect(() => {
    const isDismissed = sessionStorage.getItem('dismiss_email_verify_banner');
    if (isDismissed === 'true') {
      setDismissed(true);
    }
  }, []);

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // If user is not logged in or email is already verified or banner is dismissed, hide banner
  if (!firebaseUser || firebaseUser.emailVerified || dismissed) {
    return null;
  }

  const handleSendEmail = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      await sendVerificationEmail(firebaseUser);
      toast.success(`Verification link sent to ${firebaseUser.email}! Check your inbox.`);
      setCooldown(60);
    } catch (err: unknown) {
      const errorMsg = (err as { message?: string })?.message || 'Failed to send verification email';
      if (errorMsg.includes('too-many-requests')) {
        toast.error('Too many requests. Please wait a moment before trying again.');
        setCooldown(60);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSending(false);
    }
  };

  const handleCheckStatus = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await refreshUser();
      // Need a small tick to let state settle
      setTimeout(() => {
        if (firebaseUser.emailVerified) {
          toast.success('Your email has been verified successfully!');
        } else {
          toast.error('Email is not verified yet. Please click the link in your email and try again.');
        }
      }, 500);
    } catch {
      toast.error('Failed to refresh status');
    } finally {
      setChecking(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('dismiss_email_verify_banner', 'true');
    setDismissed(true);
  };

  return (
    <div className="bg-gradient-to-r from-amber-500/10 via-amber-400/15 to-orange-500/10 border-b border-amber-200/80 px-4 py-2.5 text-amber-900 transition-all">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2.5 text-center sm:text-left">
          <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
            <ShieldAlert size={14} />
          </div>
          <div>
            <span className="font-bold text-amber-950">Email not verified: </span>
            <span className="text-amber-800">
              Please verify <span className="font-semibold text-amber-950">{firebaseUser.email}</span> to secure your account.
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-center flex-shrink-0">
          <button
            type="button"
            onClick={handleSendEmail}
            disabled={sending || cooldown > 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <Mail size={13} />
            {sending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Send Verification Email'}
          </button>

          <button
            type="button"
            onClick={handleCheckStatus}
            disabled={checking}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/80 hover:bg-white text-amber-900 border border-amber-300/80 font-medium transition-all shadow-xs disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw size={13} className={checking ? 'animate-spin' : ''} />
            {checking ? 'Checking…' : "I've Verified"}
          </button>

          <button
            type="button"
            onClick={handleDismiss}
            className="p-1 text-amber-700 hover:text-amber-950 hover:bg-amber-200/50 rounded-md transition-colors"
            title="Dismiss for this session"
            aria-label="Dismiss banner"
          >
            <X size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
